import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoldenCircles, CornerMark } from "./GeometricDecor";
import { getLanguageFromPath } from "@/lib/language";
import { portfolioPageCopy, portfolioSliderProjects } from "@/lib/portfolio";

const initialPortfolioIndex = portfolioSliderProjects.findIndex((project) => project.slug === "nature-table");
const edgeSpacerStyle = { width: "calc(50% - clamp(7.25rem, 16vw, 13rem) / 2)" };

function getPortfolioIndexFromHash(hash: string) {
  const [target, query = ""] = hash.replace(/^#/, "").split("?");
  if (target !== "portfolio") return initialPortfolioIndex >= 0 ? initialPortfolioIndex : 0;

  const slug = new URLSearchParams(query).get("project");
  const index = portfolioSliderProjects.findIndex((project) => project.slug === slug);
  return index >= 0 ? index : initialPortfolioIndex >= 0 ? initialPortfolioIndex : 0;
}

export default function PortfolioSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(() => getPortfolioIndexFromHash(window.location.hash));
  const location = useLocation();
  const navigate = useNavigate();
  const language = getLanguageFromPath(location.pathname);
  const copy = portfolioPageCopy[language];

  useEffect(() => {
    const nextIndex = getPortfolioIndexFromHash(location.hash);
    setActiveIndex((currentIndex) => currentIndex === nextIndex ? currentIndex : nextIndex);

    if (!location.hash.startsWith("#portfolio")) return;
    const frame = window.requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const slide = slideRefs.current[activeIndex];
      const track = trackRef.current;
      if (!slide || !track) return;
      track.scrollTo({
        left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
        behavior: activeIndex === initialPortfolioIndex ? "auto" : "smooth",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeIndex]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0)";
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleProjectClick = (index: number, slug: string) => {
    if (index !== activeIndex) {
      setActiveIndex(index);
      return;
    }
    navigate(`${language === "en" ? "/en" : ""}/portfolio/${slug}`);
  };

  return (
    <section id="portfolio" ref={sectionRef} className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundColor: "#003330" }}>
      <GoldenCircles size={300} opacity={0.05} color="#acd1c6" className="-top-16 -right-16" />
      <CornerMark position="tl" size={28} color="#acd1c6" opacity={0.2} className="top-10 left-10" />
      <CornerMark position="br" size={28} color="#acd1c6" opacity={0.2} className="bottom-10 right-10" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="text-center mb-12">
          <div
            className="reveal opacity-0 translate-y-6 transition-all duration-700 inline-flex items-center gap-2 px-5 py-2 rounded-full border text-xs font-medium tracking-widest uppercase mb-6"
            style={{ borderColor: "rgba(172,209,198,0.3)", color: "#acd1c6" }}
          >
            <i className="ri-gallery-line text-sm" />
            {copy.eyebrow}
          </div>
          <h2
            className="reveal opacity-0 translate-y-6 transition-all duration-700 text-3xl md:text-4xl lg:text-5xl font-light text-white leading-tight"
            style={{ fontFamily: "'League Spartan', sans-serif" }}
          >
            {copy.titleTop}
            <br />
            <span className="font-bold italic" style={{ color: "#acd1c6" }}>{copy.titleAccent}</span>
          </h2>
        </div>

        <div
          className="reveal opacity-0 translate-y-6 transition-all duration-700 overflow-hidden py-10 -my-10"
          aria-label={copy.eyebrow}
        >
          <div
            ref={trackRef}
            className="portfolio-scroll flex items-center gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth py-2"
          >
            <div aria-hidden="true" className="shrink-0" style={edgeSpacerStyle} />
            {portfolioSliderProjects.map((project, index) => {
              const active = index === activeIndex;
              return (
                <button
                  key={project.slug}
                  type="button"
                  onClick={() => handleProjectClick(index, project.slug)}
                  ref={(element) => { slideRefs.current[index] = element; }}
                  aria-label={active ? `${project[language].title} – ${copy.eyebrow}` : `Focus ${project[language].title}`}
                  className={`group relative shrink-0 aspect-square snap-center overflow-visible transition-[width,opacity,filter] duration-500 cursor-pointer ${active ? "z-10 w-[clamp(9rem,19vw,15rem)] opacity-100" : "w-[clamp(7.25rem,16vw,13rem)] opacity-45 grayscale"}`}
                >
                  <img
                    src={project.image}
                    alt={project.alt}
                    className="block h-full w-full object-contain"
                    loading={Math.abs(index - activeIndex) < 3 ? "eager" : "lazy"}
                  />
                  <span
                    className={`absolute inset-x-0 bottom-0 px-3 py-3 text-left text-xs font-semibold text-white transition-opacity duration-300 ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                    style={{ background: "linear-gradient(transparent, rgba(0,51,48,0.9))" }}
                  >
                    {project[language].title}
                  </span>
                </button>
              );
            })}
            <div aria-hidden="true" className="shrink-0" style={edgeSpacerStyle} />
          </div>
        </div>

        <p className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center text-sm mt-8" style={{ color: "rgba(172,209,198,0.6)" }}>
          {copy.hint}
        </p>
      </div>
    </section>
  );
}
